import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'

import { fetchPrescription } from '~/api/prescriptions'
import FeatureToggle from '~/constants/featureToggle'
import { PrescriptionStates } from '~/constants/prescription'
import {
  RX_REQUEST_TASK_TYPE,
  VET_DIET_REQUEST_TASK_TYPE,
} from '~/constants/taskConstants'
import { useGetTaskTypeByTaskId } from '~/store/hooks/tasks'
import { getCurrentBusiness } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getTasksMap } from '~/store/reducers/tasks'
import type { Task } from '~/types'
import type { Prescription } from '~/types/entities/prescriptions'
import { addSearch } from '~/utils'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'

/**
 * Checks if a task type is an Rx Request task type (RX_REQUEST or VET_DIET_REQUEST).
 */
const isRxRequestTaskType = (taskType: string | null | undefined): boolean =>
  taskType === RX_REQUEST_TASK_TYPE || taskType === VET_DIET_REQUEST_TASK_TYPE

/**
 * Finds the task ID associated with a prescription by checking the prescription's
 * direct task reference or searching the tasks map for a linked task.
 */
const findTaskId = (
  prescription: Prescription,
  prescriptionId: string,
  tasksMap: Record<string, Task>,
  tasksMapValues: Task[],
): string | undefined => {
  if (prescription.task?.id) {
    return prescription.task.id
  }

  const linkedTask = tasksMapValues.find(
    (task) => task?.prescriptionId === prescriptionId,
  )
  return linkedTask?.id
}

function taskDashboardPathWithReturnContext(
  taskId: string,
  returnPath: string,
): string {
  return `/tasks-dashboard/${taskId}?from=timeline&returnPath=${encodeURIComponent(returnPath)}`
}

export interface ReviewApproveButtonProps {
  /**
   * The prescription to review and approve
   */
  prescription: Prescription
  /**
   * The name of the prescription state (from constants)
   */
  prescriptionStateName: string | null
}

/**
 * Button component that displays a "Review and Approve" button for prescriptions
 * that are pending vet approval and are Rx Request types.
 *
 * The button is only shown when:
 * - The prescription is in "Pending Vet Approval" state, AND
 * - The prescription is an Rx Request (determined by feature toggle, business type, and task type)
 *
 * When clicked, it navigates to the associated task dashboard.
 */
export const ReviewApproveButton = ({
  prescription,
  prescriptionStateName,
}: ReviewApproveButtonProps) => {
  const { t } = useTranslation(['Tasks'])
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [isLoadingTask, setIsLoadingTask] = useState(false)

  const prescriptionId = prescription.id
  const currentBusiness = useSelector(getCurrentBusiness)
  const tasksMap = useSelector(getTasksMap)
  const isFERxTrackerPhase0Enabled = useSelector(
    getFeatureToggle(FeatureToggle.RHAP_RX_STATUS_TRACKER),
  )
  const getTaskType = useGetTaskTypeByTaskId()

  const tasksMapValues = useMemo(() => Object.values(tasksMap), [tasksMap])

  const isPendingApproval =
    prescriptionStateName === PrescriptionStates.PENDING_VET_APPROVAL

  const isRxRequestPrescription = useMemo(() => {
    if (!isFERxTrackerPhase0Enabled || !currentBusiness?.omniChannel) {
      return false
    }

    const taskId = findTaskId(
      prescription,
      prescriptionId,
      tasksMap,
      tasksMapValues,
    )
    if (taskId) {
      const taskType = getTaskType(taskId)
      return isRxRequestTaskType(taskType)
    }

    return isPendingApproval
  }, [
    isFERxTrackerPhase0Enabled,
    currentBusiness?.omniChannel,
    tasksMap,
    tasksMapValues,
    getTaskType,
    prescriptionId,
    prescription.task,
    isPendingApproval,
  ])

  const showApproveReviewButton = isPendingApproval && isRxRequestPrescription

  const handleApproveAndReview = useCallback(async () => {
    if (!prescriptionId || !currentBusiness?.id) {
      return
    }

    const returnPath = searchParams.toString()
      ? `${location.pathname}?${searchParams.toString()}`
      : location.pathname

    const navigateToTask = (taskId: string) => {
      navigate(
        addSearch(
          location,
          taskDashboardPathWithReturnContext(taskId, returnPath),
        ),
      )
    }

    const taskId = findTaskId(
      prescription,
      prescriptionId,
      tasksMap,
      tasksMapValues,
    )

    if (taskId) {
      navigateToTask(taskId)
      return
    }

    setIsLoadingTask(true)
    try {
      const response = await fetchPrescription(
        currentBusiness.id,
        prescriptionId,
      )
      const fetchedPrescription =
        response?.entities?.prescriptionsV2?.[prescriptionId]
      const fetchedTaskId = fetchedPrescription?.task?.id

      if (fetchedTaskId) {
        navigateToTask(fetchedTaskId)
      }
    } catch (error) {
      // Silently handle errors from fetchPrescription
    } finally {
      setIsLoadingTask(false)
    }
  }, [
    prescriptionId,
    currentBusiness?.id,
    navigate,
    tasksMapValues,
    prescription.task,
    location.pathname,
    searchParams,
  ])

  if (!showApproveReviewButton) {
    return null
  }

  return (
    <KibButtonNew disabled={isLoadingTask} onClick={handleApproveAndReview}>
      {t('Tasks:LABEL.REVIEW_AND_APPROVE')}
    </KibButtonNew>
  )
}

/**
 * Hook that determines whether to show the review/approve button for a prescription.
 * This is used for checking visibility (e.g., for dateLabel) without rendering the button.
 *
 * @param prescription - The prescription to check.
 * @param prescriptionStateName - The name of the prescription state (from constants).
 * @returns Boolean indicating if the review/approve button should be shown.
 */
export const useShowReviewApproveButton = (
  prescription: Prescription,
  prescriptionStateName: string | null,
): boolean => {
  const currentBusiness = useSelector(getCurrentBusiness)
  const tasksMap = useSelector(getTasksMap)
  const isFERxTrackerPhase0Enabled = useSelector(
    getFeatureToggle(FeatureToggle.RHAP_RX_STATUS_TRACKER),
  )
  const getTaskType = useGetTaskTypeByTaskId()

  const tasksMapValues = useMemo(() => Object.values(tasksMap), [tasksMap])

  const isPendingApproval =
    prescriptionStateName === PrescriptionStates.PENDING_VET_APPROVAL

  const isRxRequestPrescription = useMemo(() => {
    if (!isFERxTrackerPhase0Enabled || !currentBusiness?.omniChannel) {
      return false
    }

    const taskId = findTaskId(
      prescription,
      prescription.id,
      tasksMap,
      tasksMapValues,
    )
    if (taskId) {
      const taskType = getTaskType(taskId)
      return isRxRequestTaskType(taskType)
    }

    return isPendingApproval
  }, [
    isFERxTrackerPhase0Enabled,
    currentBusiness?.omniChannel,
    tasksMap,
    tasksMapValues,
    getTaskType,
    prescription.id,
    prescription.task,
    isPendingApproval,
  ])

  return isPendingApproval && isRxRequestPrescription
}
